import { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ChevronsUpDown, Plus, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVendors, getComplianceStatus } from "@/hooks/useVendors";
import { NewVendorDialog } from "./NewVendorDialog";

interface Props {
  value: string;
  onValueChange: (id: string) => void;
  placeholder?: string;
}

const complianceDot: Record<string, string> = {
  green: "text-green-500",
  yellow: "text-yellow-500",
  red: "text-red-500",
};

export const VendorSelectWithCreate = ({ value, onValueChange, placeholder = "Select vendor" }: Props) => {
  const { data: vendors } = useVendors();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitialName, setDialogInitialName] = useState("");

  const selectedName = useMemo(() => {
    return vendors?.find((v) => v.id === value)?.name || "";
  }, [vendors, value]);

  const filtered = useMemo(() => {
    if (!vendors) return [];
    if (!search.trim()) return vendors;
    return vendors.filter((v) => v.name.toLowerCase().includes(search.toLowerCase()));
  }, [vendors, search]);

  const exactMatch = useMemo(() => {
    if (!search.trim() || !vendors) return true;
    return vendors.some((v) => v.name.toLowerCase() === search.toLowerCase());
  }, [vendors, search]);

  const handleCreateClick = () => {
    setDialogInitialName(search);
    setOpen(false);
    setDialogOpen(true);
  };

  const handleCreated = (vendor: { id: string; name: string }) => {
    onValueChange(vendor.id);
    setSearch("");
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal">
            {selectedName || <span className="text-muted-foreground">{placeholder}</span>}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search vendors…" value={search} onValueChange={setSearch} />
            <CommandList>
              <CommandEmpty className="py-2 px-3 text-sm text-muted-foreground">No vendors found.</CommandEmpty>
              <CommandGroup>
                {filtered.map((v) => {
                  const compliance = getComplianceStatus(v as any);
                  return (
                    <CommandItem key={v.id} value={v.id} onSelect={() => { onValueChange(v.id); setOpen(false); setSearch(""); }}>
                      <Check className={cn("mr-2 h-4 w-4", value === v.id ? "opacity-100" : "opacity-0")} />
                      <span className="flex-1">{v.name}</span>
                      {compliance.level !== "green" && (
                        <span className={cn("text-xs ml-2", complianceDot[compliance.level])}>
                          {compliance.level === "red" ? "🔴" : "⚠️"} {compliance.issues[0]?.label}
                        </span>
                      )}
                      {compliance.level === "green" && <span className="text-xs ml-2 text-green-500">🟢</span>}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {search.trim() && !exactMatch && (
                <CommandGroup>
                  <CommandItem onSelect={handleCreateClick} className="text-primary">
                    <Plus className="mr-2 h-4 w-4" />
                    Create &quot;{search}&quot;
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <NewVendorDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initialName={dialogInitialName}
        onCreated={handleCreated}
      />
    </>
  );
};
